/**
 * Dokumenttypen einer Bestellung - 1:1-Portierung aus `index.html` (Stand 2026-08-08).
 *
 * Alles hier beschreibt Papiere, die zu einer Bestellung gehoeren: die Pflicht-Checkliste, die
 * per OCR auswertbaren Typen und den CMR-Typ, der nur bei Sammellieferungen auftaucht.
 *
 * Werte woertlich uebernommen, ergaenzt wurden ausschliesslich Typen. Einzige Abweichung vom Bestand:
 * `DOC_TYPES` fuehrt statt des einen `zahlungsbeleg` den Anzahlungsbeleg und den Restzahlungs-/
 * Gesamtzahlungsbeleg; die Split-Typen (`SPLIT_PAYMENT_DOC_TYPES`) entfallen.
 */

export interface DocType {
  id: string;
  label: string;
  /** Nur gesetzt, wenn der Typ nicht automatisch erkannt, sondern nur von Hand zugeordnet wird. */
  manualOnly?: boolean;
  /** Nur gesetzt, wenn das Dokument fuer die Vollstaendigkeit nicht zaehlt. */
  optional?: boolean;
}

/**
 * Pflicht-Dokumente je Bestellung (abhakbare Checkliste) - index.html:4448-4466, Kommentar
 * woertlich uebernommen. Reihenfolge ist die Anzeigereihenfolge der Checkliste.
 *
 * Die Eintraege sind bewusst nicht vereinheitlicht: `manualOnly` und `optional` stehen nur dort, wo
 * das Original sie setzt. Ein ergaenztes `optional: false` waere eine Wertaenderung.
 *
 * ABWEICHUNG vom Original (Asana 1220057765558651, Spec 2026-10-31): index.html:4448-4466 fuehrt hier
 * den einen Eintrag `zahlungsbeleg` ("Zahlungsbeleg"). An seiner Stelle stehen, in dieser Reihenfolge,
 * `anzahlungsbeleg` ("Anzahlungsbeleg") und `restzahlungsbeleg` ("Restzahlungs-/Gesamtzahlungsbeleg");
 * der Restzahlungsbeleg ist der Nachfolger des alten `zahlungsbeleg` (letzte oder einzige Rate). Ob der
 * Anzahlungsbeleg in der Checkliste steht und zaehlt, entscheiden `visibleDocTypes` und `docProgress`
 * (`lib/status.ts`) nach Ratenzahl und Merker, nicht diese Liste.
 */
export const DOC_TYPES: readonly DocType[] = [
  { id: "bestellschein", label: "Bestellschein / PO" },
  { id: "pi", label: "PI · Proforma Invoice" },
  { id: "ci", label: "CI · Commercial Invoice" },
  { id: "pl", label: "PL · Packliste" },
  { id: "bl_hawbl", label: "BL / HAWBL" },
  { id: "co", label: "CO · Ursprungszeugnis" },
  { id: "steuerbescheid", label: "Steuerbescheid · Einfuhrabgabenbescheid" },
  { id: "zollanweisung", label: "Zollanweisung" },
  { id: "speditionsangebot", label: "Speditionsangebot" },
  { id: "speditionsrechnung", label: "Speditionsrechnung" },
  { id: "speditionsgebuehren", label: "Speditionsgebühren", optional: true },
  { id: "zollrechnung", label: "Zollrechnung" },
  { id: "anzahlungsbeleg", label: "Anzahlungsbeleg" },
  { id: "restzahlungsbeleg", label: "Restzahlungs-/Gesamtzahlungsbeleg" },
  { id: "gutschrift", label: "Gutschrift", optional: true },
];

export interface DocumentOcrType {
  label: string;
  /** Beschriftung des Betragsfelds im Pruefdialog. */
  amountLabel: string;
  /** Feld der Bestellung, in das der erkannte Betrag geschrieben wird. */
  target: string;
  /** Grobe Einordnung fuer die Weiterverarbeitung. */
  kind: string;
}

/**
 * Dokumenttypen, fuer die es eine OCR-Auswertung gibt (index.html:4467-4473). Die Schluessel sind
 * `DocType.id`-Werte; die Schluesselreihenfolge ist die Reihenfolge im Auswahlmenue.
 */
export const DOCUMENT_OCR_TYPES: Readonly<Record<string, DocumentOcrType>> = {
  ci: { label: "CI / Sammelrechnung", amountLabel: "Rechnungsbetrag", target: "supplierInvoice", kind: "ci" },
  steuerbescheid: {
    label: "Einfuhrabgabenbescheid",
    amountLabel: "Zollbetrag",
    target: "customsInvoice",
    kind: "customs",
  },
  zollrechnung: {
    label: "Zollrechnung",
    amountLabel: "Zollbetrag / Rechnungsbetrag",
    target: "customsInvoice",
    kind: "customs",
  },
  speditionsrechnung: {
    label: "Transport- / Speditionsrechnung",
    amountLabel: "Transportbetrag netto",
    target: "freightInvoice",
    kind: "freight",
  },
  speditionsgebuehren: {
    label: "Speditionsgebühren",
    amountLabel: "Speditionsgebühren",
    target: "freightFees",
    kind: "freight",
  },
};

// index.html:4474-4477 (`SPLIT_PAYMENT_DOC_TYPES`: `zahlungsbeleg_anzahlung`, `zahlungsbeleg_restzahlung`) bewusst nicht
// uebernommen: die beiden Typen sind durch `anzahlungsbeleg` und `restzahlungsbeleg` in `DOC_TYPES` ersetzt (Asana
// 1220057765558651, Spec 2026-10-31).

/** Frachtbrief, der nur bei Sammellieferungen zur Checkliste kommt (index.html:4478). */
export const CMR_DOC_TYPE: Readonly<DocType> = { id: "cmr", label: "CMR-Dokument", optional: true };
